import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, MessageSquare, ShieldCheck, AlertTriangle, Save, Send, Trash2, Lock, Beaker } from "lucide-react";
import { Integrations } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

const EMPTY = { sms_enabled: true, twilio_account_sid: "", twilio_auth_token: "", twilio_from_number: "" };

export default function IntegrationsPanel() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [emailInfo, setEmailInfo] = useState(null);
  const [draft, setDraft] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [emailTesting, setEmailTesting] = useState(false);
  const [testTo, setTestTo] = useState("");

  const load = async () => {
    try {
      const d = await Integrations.me();
      setData(d);
      setDraft({
        sms_enabled: d.sms.enabled || d.sms.configured || true,
        twilio_account_sid: d.sms.twilio_account_sid_masked || "",
        twilio_auth_token: d.sms.twilio_auth_token_masked || "",
        twilio_from_number: d.sms.twilio_from_number || "",
      });
      if (isAdmin) {
        try { setEmailInfo(await Integrations.emailStatus()); } catch { /* */ }
      }
    } catch { toast.error("Não foi possível carregar integrações"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [isAdmin]);

  const testEmail = async () => {
    setEmailTesting(true);
    try {
      const r = await Integrations.testEmail();
      toast.success("Email de teste enviado", { description: `Para ${r.to} · verifica a tua caixa.` });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao enviar email");
    } finally { setEmailTesting(false); }
  };

  const save = async () => {
    if (!draft.twilio_account_sid || !draft.twilio_auth_token || !draft.twilio_from_number) {
      toast.error("Preenche os 3 campos");
      return;
    }
    if (!draft.twilio_from_number.startsWith("+")) {
      toast.error("Número remetente em formato E.164 (ex: +351...)");
      return;
    }
    setBusy(true);
    try {
      await Integrations.setTwilio(draft);
      toast.success("Configuração SMS guardada");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha ao guardar"); }
    finally { setBusy(false); }
  };

  const clear = async () => {
    if (!window.confirm("Remover credenciais Twilio?")) return;
    try {
      await Integrations.clearTwilio();
      setDraft(EMPTY); await load();
      toast.success("Credenciais removidas");
    } catch { toast.error("Não foi possível remover"); }
  };

  const test = async () => {
    if (!testTo.startsWith("+")) { toast.error("Número de destino E.164 (ex: +351...)"); return; }
    setTesting(true);
    try {
      await Integrations.testSms(testTo);
      toast.success("SMS de teste enviado", { description: "Verifica o telemóvel." });
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha no envio"); }
    finally { setTesting(false); }
  };

  const smsConfigured = data?.sms?.configured;
  const emailReady = data?.email?.configured;

  return (
    <div data-testid="integrations-panel" className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-sky-400 font-semibold">Comunicação</div>
        <h2 className="font-display text-2xl text-slate-50 mt-1">Integrações de Comunicação</h2>
        <p className="text-slate-400 text-sm mt-1">
          Configure os seus canais de comunicação. Os emails são geridos pela plataforma. Os SMS são enviados através da sua conta pessoal.
        </p>
      </div>

      {/* EMAIL CARD (read-only para terapeuta; admin tem teste + status) */}
      <div data-testid="email-integration-card" className="rounded-2xl border border-emerald-500/20 bg-slate-900/50 p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3 min-w-0 flex-1">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center"><Mail className="w-5 h-5 text-emerald-300" /></div>
            <div className="min-w-0">
              <div className="text-slate-50 font-medium flex items-center gap-2 flex-wrap">
                Email
                <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-300 border border-emerald-500/30">Gerido pela plataforma</span>
                {isAdmin && emailInfo?.is_sandbox && (
                  <span data-testid="email-sandbox-badge" className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-300 border border-amber-500/30 inline-flex items-center gap-1">
                    <Beaker className="w-3 h-3" /> Sandbox
                  </span>
                )}
              </div>
              <div className="text-[12px] text-slate-400 mt-1">Convites, lembretes e avisos transacionais são enviados pelo MIO. Sem custos para si.</div>
              <div className="mt-2 inline-flex items-center gap-2 text-[11px] text-slate-500">
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
                Status: {emailReady ? <span className="text-emerald-300">ativo</span> : <span className="text-amber-300">a aguardar configuração da plataforma</span>}
              </div>
              {isAdmin && emailInfo && (
                <div data-testid="email-admin-info" className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2 text-[11px] text-slate-500 border-t border-slate-800/60 pt-3">
                  <div><span className="text-slate-600">RESEND_API_KEY:</span> <span className="font-mono text-slate-300">{emailInfo.configured ? emailInfo.key_masked : "—"}</span></div>
                  <div><span className="text-slate-600">FROM:</span> <span className="font-mono text-slate-300">{emailInfo.from_email}</span></div>
                  {emailInfo.is_sandbox && (
                    <div className="md:col-span-2 text-amber-300/80 text-[11px] mt-1">
                      ⓘ Em modo sandbox apenas envia para o email da conta Resend. Para produção, verifica um domínio em <span className="font-mono">resend.com/domains</span> e atualiza <span className="font-mono">RESEND_FROM_EMAIL</span> no backend.
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          {isAdmin && (
            <Button
              data-testid="test-email-btn"
              onClick={testEmail}
              disabled={emailTesting || !emailInfo?.configured}
              variant="ghost"
              title={emailInfo?.configured ? "Enviar email de teste para o teu email" : "RESEND_API_KEY em falta"}
              className="rounded-full text-emerald-300 hover:bg-emerald-500/10"
            >
              <Send className="w-4 h-4 mr-1.5" /> {emailTesting ? "A enviar…" : "Testar email"}
            </Button>
          )}
        </div>
      </div>

      {/* SMS CARD (BYO) */}
      <div data-testid="sms-integration-card" className="rounded-2xl border border-sky-500/20 bg-slate-900/50 p-6 space-y-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-sky-500/10 border border-sky-500/30 flex items-center justify-center"><MessageSquare className="w-5 h-5 text-sky-300" /></div>
            <div>
              <div className="text-slate-50 font-medium flex items-center gap-2">
                SMS · Twilio
                {smsConfigured ? (
                  <span data-testid="sms-status-ok" className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-300 border border-emerald-500/30">Configurado</span>
                ) : (
                  <span data-testid="sms-status-pending" className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-300 border border-amber-500/30 inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3"/>Não configurado</span>
                )}
              </div>
              <div className="text-[12px] text-slate-400 mt-1 max-w-xl">
                Cada terapeuta usa a sua própria conta Twilio. <span className="text-slate-300">Os custos de SMS são cobrados diretamente pela Twilio</span>, não pelo MIO.
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-slate-300 text-xs">Ativo</Label>
            <Switch
              data-testid="sms-enabled-toggle"
              checked={!!draft.sms_enabled}
              onCheckedChange={(v) => setDraft({ ...draft, sms_enabled: v })}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-slate-300 text-xs">Account SID</Label>
            <Input
              data-testid="twilio-sid-input"
              value={draft.twilio_account_sid}
              onChange={(e) => setDraft({ ...draft, twilio_account_sid: e.target.value })}
              placeholder="AC1234..."
              className="bg-slate-950 border-slate-800 text-slate-50 mt-1 font-mono text-xs"
            />
          </div>
          <div>
            <Label className="text-slate-300 text-xs flex items-center gap-1.5"><Lock className="w-3 h-3 text-slate-500"/> Auth Token</Label>
            <Input
              data-testid="twilio-token-input"
              type="text"
              value={draft.twilio_auth_token}
              onChange={(e) => setDraft({ ...draft, twilio_auth_token: e.target.value })}
              placeholder="••••••••"
              className="bg-slate-950 border-slate-800 text-slate-50 mt-1 font-mono text-xs"
            />
          </div>
          <div>
            <Label className="text-slate-300 text-xs">Número remetente (E.164)</Label>
            <Input
              data-testid="twilio-from-input"
              value={draft.twilio_from_number}
              onChange={(e) => setDraft({ ...draft, twilio_from_number: e.target.value })}
              placeholder="+351912345678"
              className="bg-slate-950 border-slate-800 text-slate-50 mt-1 font-mono text-xs"
            />
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 flex-wrap pt-2 border-t border-slate-800/60">
          <div className="flex items-center gap-2">
            {smsConfigured && (
              <button data-testid="twilio-clear-btn" onClick={clear} className="inline-flex items-center gap-1.5 text-xs text-rose-300 hover:text-rose-200">
                <Trash2 className="w-3.5 h-3.5" /> Remover credenciais
              </button>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Input
              data-testid="test-sms-to"
              value={testTo}
              onChange={(e) => setTestTo(e.target.value)}
              placeholder="+351..."
              className="bg-slate-950 border-slate-800 text-slate-50 max-w-[12rem] font-mono text-xs"
            />
            <Button
              data-testid="test-sms-btn"
              variant="ghost"
              onClick={test}
              disabled={testing || !smsConfigured}
              title={smsConfigured ? "Enviar SMS de teste" : "Configura primeiro"}
              className="rounded-full text-sky-300 hover:bg-sky-500/10"
            >
              <Send className="w-4 h-4 mr-1.5" /> {testing ? "A enviar…" : "Testar"}
            </Button>
            <Button
              data-testid="twilio-save-btn"
              onClick={save}
              disabled={busy}
              className="bg-sky-500 hover:bg-sky-400 text-slate-950 rounded-full px-5"
            >
              <Save className="w-4 h-4 mr-2" /> {busy ? "A guardar…" : "Guardar"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
